import type { RoleName } from "../types.ts";

export type PanelId =
	| "kpi"
	| "progress-curve"
	| "stage-bars"
	| "stage-status"
	| "measurement-history"
	| "attendance"
	| "weather"
	| "recent-posts"
	| "client-hero"
	| "milestones"
	| "presence"
	| "in-progress-stages"
	| "new-post";

export interface PanelSpec {
	id: PanelId;
	permissions: string[];
	roles?: RoleName[];
	excludeRoles?: RoleName[];
}

export const OBRA_PANELS: PanelSpec[] = [
	{
		id: "client-hero",
		permissions: ["charts:construction_progress:read"],
		roles: ["cliente"],
	},
	{
		id: "presence",
		permissions: ["time_entries:clock"],
		roles: ["funcionario", "almoxarife"],
	},
	{
		id: "kpi",
		permissions: ["charts:construction_progress:read"],
		excludeRoles: ["cliente", "funcionario"],
	},
	{
		id: "weather",
		permissions: [],
	},
	{
		id: "progress-curve",
		permissions: ["charts:construction_progress:read"],
		excludeRoles: ["cliente", "funcionario", "almoxarife"],
	},
	{
		id: "stage-status",
		permissions: ["charts:stage_status:read"],
		excludeRoles: ["cliente"],
	},
	{
		id: "stage-bars",
		permissions: ["charts:construction_progress:read"],
		excludeRoles: ["cliente", "funcionario", "almoxarife"],
	},
	{
		id: "in-progress-stages",
		permissions: ["construction_stage_schedules:view"],
		roles: ["funcionario", "almoxarife"],
	},
	{
		id: "milestones",
		permissions: ["construction_stage_schedules:view"],
		roles: ["cliente"],
	},
	{
		id: "measurement-history",
		permissions: ["charts:measurements:read"],
	},
	{
		id: "attendance",
		permissions: ["charts:attendance:read"],
	},
	{
		id: "new-post",
		permissions: ["posts:create"],
		roles: ["funcionario"],
	},
	{
		id: "recent-posts",
		permissions: ["posts:list"],
	},
];

export interface PanelViewer {
	has: (permission: string) => boolean;
	role: RoleName | null;
}

export function isPanelVisible(spec: PanelSpec, viewer: PanelViewer): boolean {
	if (viewer.role && spec.excludeRoles?.includes(viewer.role)) return false;
	if (spec.roles && (!viewer.role || !spec.roles.includes(viewer.role))) {
		return false;
	}
	return spec.permissions.every((permission) => viewer.has(permission));
}

export function resolvePanels(
	specs: PanelSpec[],
	viewer: PanelViewer,
): PanelId[] {
	return specs
		.filter((spec) => isPanelVisible(spec, viewer))
		.map((spec) => spec.id);
}

const ROLE_NAMES: RoleName[] = [
	"engineer",
	"almoxarife",
	"funcionario",
	"cliente",
];

export function toRoleName(value: string | undefined): RoleName | null {
	const normalized = value?.trim().toLowerCase() ?? "";
	return ROLE_NAMES.find((role) => role === normalized) ?? null;
}
